import axiosInstance from "@/axios/axiosInstance";
import type {
  GetConversationListRequest,
  GetConversationListResponse,
  GetMessageHistoryRequest,
  GetMessageHistoryResponse,
} from "@/interface/message.types";

/**
 * Service for message-related API calls
 * Handles REST API integration for conversation list and message history
 */

/**
 * Get conversation list for the current user
 * @param request - Optional request parameters for pagination, filtering, etc.
 * @returns Promise with conversation list response
 */
export const getConversationList = async (
  request?: Partial<GetConversationListRequest>
): Promise<GetConversationListResponse> => {
  try {
    const defaultBody: GetConversationListRequest = {
      pageNumber: 0,
      pageSize: 50,
      query: "",
      filters: "",
      sorts: "",
    };

    const response = await axiosInstance.post<GetConversationListResponse>(
      "/Conversation/GetConversationList",
      { ...defaultBody, ...request }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch conversation list:", error);
    throw error;
  }
};

/**
 * Get message history for a specific conversation
 * @param proposalId - The proposal ID (required) - identifies which conversation
 * @param request - Optional request parameters for pagination, filtering, etc.
 * @returns Promise with message history response
 */
export const getMessageHistory = async (
  proposalId: string,
  request?: Partial<GetMessageHistoryRequest>
): Promise<GetMessageHistoryResponse> => {
  try {
    const defaultBody: GetMessageHistoryRequest = {
      pageNumber: 0,
      pageSize: 10,
      query: "",
      filters: "",
      sorts: "",
      proposalId, // Required parameter
    };

    const response = await axiosInstance.post<GetMessageHistoryResponse>(
      "/Conversation/GetMessageHistory",
      { ...defaultBody, ...request }
    );

    // Normalize messages: Reverse from descending (API default) to ascending (UI/Socket expected)
    // This ensures that appending new messages via SignalR results in a correct timeline
    if (response.data?.data?.messages) {
      response.data.data.messages.reverse();
    }

    return response.data;
  } catch (error) {
    console.error("Failed to fetch message history:", error);
    throw error;
  }
};
